// src/components/themes/default/admin/AddCategoryModal.tsx
"use client";

import { useState } from "react";
import {
  X,
  Layers,
  Upload,
  Sparkles,
  Loader2,
  FolderPlus,
  Smile,
} from "lucide-react";

interface AddCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subdomain: string;
  branchId?: number;
  primaryColor?: string;
}

export default function AddCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  subdomain,
  branchId,
  primaryColor = "#059669",
}: AddCategoryModalProps) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("📦");
  const [showOnHome, setShowOnHome] = useState(true);
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAiDescription = (type: "short" | "detailed") => {
    if (!name.trim()) {
      setError("Age category name likhun, tarpor AI click korun.");
      return;
    }
    if (type === "short") {
      setDescription(`Curated premium collection of ${name.trim()} crafted for exceptional quality & daily needs.`);
    } else {
      setDescription(`Explore our exclusive outlet range of ${name.trim()}. Handpicked directly from verified suppliers to ensure freshness, premium grade reliability, and unbeatable branch value.`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const token =
        localStorage.getItem("merchant_token") ||
        localStorage.getItem("token") ||
        "";

      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");

      const apiUrl = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/stores/categories/`
        : `/api/v1/stores/categories/`;

      const slug = name
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");

      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("slug", slug);
      formData.append("description", description.trim());
      formData.append("icon", icon.trim());
      formData.append("show_on_homepage", String(showOnHome));
      formData.append("show_in_nav", "true");

      if (branchId) {
        formData.append("branch_id", String(branchId));
      }

      if (imageFile) {
        formData.append("image", imageFile);
      }

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.name?.[0] || "Category create korte problem hoyeche.");
      }

      onSuccess();
      onClose();
      setName("");
      setDescription("");
      setIcon("📦");
      setImageFile(null);
      setImagePreview(null);
    } catch (err: any) {
      setError(err.message || "Failed to create category");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="w-full max-w-md rounded-3xl bg-[#090d16] border border-slate-800/80 text-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              <FolderPlus className="w-4 h-4" />
            </div>
            <h2 className="text-sm font-black tracking-wide">Add New Category</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form
          id="add-branch-category-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar"
        >
          {error && (
            <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold leading-relaxed">
              {error}
            </div>
          )}

          {/* Category Name */}
          <div>
            <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
              Category Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Food, Fashion, Electronics, Grocery, Bakery, Sports"
              className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-[#0d1424] border border-cyan-500/40 text-white placeholder-slate-500 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 transition-all"
            />
          </div>

          {/* Icon/Emoji & Homepage Grid */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
                Icon / Emoji
              </label>
              <div className="flex items-center gap-2 bg-[#0d1424] border border-slate-800 rounded-2xl p-1.5 px-3">
                <span className="text-base">{icon}</span>
                <input
                  type="text"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  placeholder="📦"
                  className="w-full text-xs font-bold bg-transparent text-white outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
                Homepage Grid
              </label>
              <label className="flex items-center gap-2 bg-[#0d1424] border border-slate-800 rounded-2xl p-2.5 px-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnHome}
                  onChange={(e) => setShowOnHome(e.target.checked)}
                  className="w-4 h-4 accent-cyan-500 rounded cursor-pointer"
                />
                <span className="text-xs font-bold text-slate-300">Show on Home</span>
              </label>
            </div>
          </div>

          {/* Description with AI Assistant */}
          <div>
            <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
              <label className="text-[11px] font-bold text-slate-300">Description</label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleAiDescription("short")}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-[9px] font-black cursor-pointer transition-all"
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  <span>AI Auto-Write (Short)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleAiDescription("detailed")}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 text-[9px] font-black cursor-pointer transition-all"
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  <span>AI Auto-Write (Detailed)</span>
                </button>
              </div>
            </div>

            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short category description..."
              className="w-full p-3 rounded-2xl text-xs font-bold bg-[#0d1424] border border-slate-800 text-white placeholder-slate-500 outline-none focus:border-cyan-500/50 resize-none transition-all"
            />
          </div>

          {/* Category Banner Image */}
          <div>
            <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
              Category Banner Image (Optional)
            </label>
            <div className="relative border-2 border-dashed border-slate-800 hover:border-cyan-500/40 rounded-2xl p-4 text-center transition-colors bg-[#0d1424]/50">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              {imagePreview ? (
                <div className="relative w-full h-24 rounded-xl overflow-hidden">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/80 text-[10px] text-emerald-400 font-bold">
                    ✓ Selected
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 py-1 text-slate-400">
                  <Upload className="w-4 h-4 text-rose-500" />
                  <span className="text-xs font-bold text-slate-300">Choose image file</span>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800/60 bg-[#070a12] shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-2xl text-xs font-bold text-slate-400 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-branch-category-form"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black text-white bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 shadow-lg shadow-rose-600/30 transition-all disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Category</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}